/**
 * Site photography (owner upgrade brief 2026-09-27 §2.3; phase U4). The data comes from
 * lib/sitePhotoData.js, generated by scripts/site_photos.py from the imagery library; the files
 * live in public/images/site/photos/<id>-<ratio>-<width>.webp (WebP only).
 *
 * Every photo in the library is AI-generated (track GEN): never caption one as a real customer,
 * venue, advertiser or the founder. Coaster faces are blank until the approved art is
 * composited (re-run the script with --overrides; no code change).
 *
 * - sitePhoto(id) → { id, alt, ratios } or null
 * - photoSources(id, ratio) → { src, srcSet, width, height, focal } for one ratio, or null
 * - categoryPhotoId(category) → CAT-01…09 for a business's top-level category (slug, { slug } or name)
 * - venuePhotoId(venue) → VENUE-FALLBACK-01 bar / 02 restaurant / 03 brewery
 */
import SITE_PHOTO_DATA from './sitePhotoData';

export const SITE_PHOTO_DIR = '/images/site/photos';

export function sitePhoto(id) {
  const entry = Object.prototype.hasOwnProperty.call(SITE_PHOTO_DATA, id) ? SITE_PHOTO_DATA[id] : null;
  return entry ? { id, alt: entry.alt, ratios: entry.ratios } : null;
}

export const photoUrl = (id, ratio, width) => `${SITE_PHOTO_DIR}/${id.toLowerCase()}-${ratio}-${width}.webp`;

/** One ratio of a photo, ready for <img>/<source>: the smallest width as `src`. */
export function photoSources(id, ratio) {
  const photo = sitePhoto(id);
  const r = photo && (photo.ratios[ratio] || null);
  if (!r) return null;
  return {
    src: photoUrl(id, ratio, r.widths[Math.min(1, r.widths.length - 1)]),
    srcSet: r.widths.map((w) => `${photoUrl(id, ratio, w)} ${w}w`).join(', '),
    width: r.width,
    height: r.height,
    focal: `${r.focal[0]}% ${r.focal[1]}%`,
  };
}

/** The first ratio a photo has among `preferred` (e.g. ['4x5', '1x1']), else its first one. */
export function pickRatio(id, preferred = []) {
  const photo = sitePhoto(id);
  if (!photo) return null;
  return preferred.find((r) => photo.ratios[r]) || Object.keys(photo.ratios)[0];
}

/**
 * The nine top-level categories of the free-listing form, in the brief's CAT order. `food`
 * (legacy businesses only; not offered on the form) takes the restaurant venue photo; anything
 * unknown takes Retail's shop shelves, the most general "local business" scene.
 */
export const CATEGORY_PHOTO_IDS = Object.freeze({
  'professional-services': 'CAT-01',
  'local-services': 'CAT-02',
  retail: 'CAT-03',
  wellness: 'CAT-04',
  beauty: 'CAT-05',
  'garden-outdoor': 'CAT-06',
  'fun-entertainment': 'CAT-07',
  tech: 'CAT-08',
  auto: 'CAT-09',
  food: 'VENUE-FALLBACK-02',
  'food-dining': 'VENUE-FALLBACK-02',
});
export const DEFAULT_CATEGORY_PHOTO_ID = 'CAT-03';

/** A slug from a slug, a `{ slug }` ref or a display name ("Garden & Outdoor" → garden-outdoor; the profile sends names). */
const slugOf = (v) => {
  const raw = typeof v === 'string' ? v : (v && typeof v === 'object' && typeof v.slug === 'string' ? v.slug : '');
  return raw.trim().toLowerCase().replace(/&/g, ' ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
};

export function categoryPhotoId(category) {
  const slug = slugOf(category);
  return Object.prototype.hasOwnProperty.call(CATEGORY_PHOTO_IDS, slug) ? CATEGORY_PHOTO_IDS[slug] : DEFAULT_CATEGORY_PHOTO_ID;
}

/**
 * The fallback photo for a venue without its own picture, by venue type: a `venue_type` field
 * when the record has one, else the name ("Brewing", "Taproom" → brewery; "Restaurant",
 * "Kitchen", "Grill", "Diner", "Café", "Bistro", "Trattoria", "Pizzeria" → restaurant), else bar.
 */
export function venuePhotoId(venue) {
  const type = slugOf(venue?.venue_type || venue?.type);
  const name = typeof venue?.name === 'string' ? venue.name.toLowerCase() : '';
  const probe = `${type} ${name}`;
  if (/brew|taproom|tap room/.test(probe)) return 'VENUE-FALLBACK-03';
  if (/restaurant|kitchen|grill|diner|caf[eé]|bistro|trattoria|pizzeria|eatery/.test(probe)) return 'VENUE-FALLBACK-02';
  return 'VENUE-FALLBACK-01';
}
